<template>
  <div class="h-100">
    <div class="row m-0 p-0 align-items-center mb-2">
      <div class="col-auto p-0">
        <div class="d-inline-block page-title-box-spacing">
          <div class="d-flex align-items-center topbar-right-spacing name-header">
            Հաշվետվություններ
          </div>
        </div>
      </div>
    </div>

    <!-- Group nav -->
    <div class="d-flex flex-wrap align-items-center mb-2 border-bottom pb-2 gap-2">
      <ul class="nav report-group-nav mr-2 mb-1">
        <li v-for="g in groups" :key="g.key" class="nav-item">
          <a class="nav-link py-1 px-3" :class="activeGroup === g.key ? 'active' : ''" href="#" @click.prevent="setGroup(g.key)">
            {{ g.label }}
          </a>
        </li>
      </ul>
      <ul v-if="currentGroupTabs.length > 1" class="nav nav-tabs suggestions-tab align-items-end mb-1">
        <li v-for="t in currentGroupTabs" :key="t.key" class="nav-item">
          <a class="nav-link py-1" :class="activeTab === t.key ? 'active' : ''" href="#" @click.prevent="setTab(t.key)">
            {{ t.label }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Filters (not shown for dashboard) -->
    <report-filters v-if="!['dashboard'].includes(activeGroup)" @filter="onFilter">
      <template #actions>
        <button v-if="activeGroup === 'planning'" @click="exportExcel" class="btn btn-sm btn-success ml-2">
          <i class="mdi mdi-microsoft-excel mr-1"></i> Excel
        </button>
      </template>
    </report-filters>

    <!-- ============================
         GROUP 1: PLANNING
         ============================ -->

    <!-- 1.1 Procurement Plan -->
    <div v-if="activeGroup==='planning' && activeTab==='planning'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted mr-3">Ընդամենը: <strong>{{ total }}</strong></small>
          <div class="ml-auto">
            <column-selector :all-columns="allColumns.planning" :visible.sync="visibleColumns.planning" storage-key="rpt_planning" />
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th v-if="isCol('planning','cpv_code')">CPV</th>
              <th v-if="isCol('planning','cpv_name')">Անվանում</th>
              <th v-if="isCol('planning','unit')">Չ/մ</th>
              <th v-if="isCol('planning','planned_count')" class="text-right">Պլ. քան.</th>
              <th v-if="isCol('planning','unit_amount')" class="text-right">Մ. գ.</th>
              <th v-if="isCol('planning','planned_value')" class="text-right">Պլ. արժ.</th>
              <th v-if="isCol('planning','organized_itender')" class="text-right">iTender</th>
              <th v-if="isCol('planning','organized_outside')" class="text-right">Արտ.</th>
              <th v-if="isCol('planning','remaining')" class="text-right">Մնաց.</th>
              <th v-if="isCol('planning','execution_pct')" class="text-right">%</th>
              <th v-if="isCol('planning','classifier')">Ստ.</th>
              <th v-if="isCol('planning','status')">Կ.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="12" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="12" class="text-center py-4 text-muted">Տվ. չկան</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else>
                <td v-if="isCol('planning','cpv_code')"><small>{{ row.cpv_code }}</small></td>
                <td v-if="isCol('planning','cpv_name')" style="max-width:260px"><span :title="row.cpv_name">{{ row.cpv_name }}</span></td>
                <td v-if="isCol('planning','unit')">{{ row.unit }}</td>
                <td v-if="isCol('planning','planned_count')" class="text-right">{{ row.planned_count | numeral('0,0.##') }}</td>
                <td v-if="isCol('planning','unit_amount')" class="text-right">{{ row.unit_amount | numeral('0,0') }}դ.</td>
                <td v-if="isCol('planning','planned_value')" class="text-right font-weight-bold">{{ row.planned_value | numeral('0,0') }}դ.</td>
                <td v-if="isCol('planning','organized_itender')" class="text-right text-primary">{{ row.organized_itender | numeral('0,0.##') }}</td>
                <td v-if="isCol('planning','organized_outside')" class="text-right text-secondary">{{ row.organized_outside | numeral('0,0.##') }}</td>
                <td v-if="isCol('planning','remaining')" class="text-right" :class="row.remaining>0?'text-warning':'text-success'">{{ row.remaining | numeral('0,0.##') }}</td>
                <td v-if="isCol('planning','execution_pct')" class="text-right"><span class="badge" :class="pctBadge(row.execution_pct)">{{ row.execution_pct }}%</span></td>
                <td v-if="isCol('planning','classifier')"><small>{{ row.classifier||'—' }}</small></td>
                <td v-if="isCol('planning','status')"><span class="badge" :class="planStatusBadge(row.status)">{{ planStatusLabel(row.status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- 1.2 Plan vs Organized -->
    <div v-if="activeGroup==='planning' && activeTab==='plan-vs-organized'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Ընդամենը: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>CPV</th><th>Անվ.</th>
              <th class="text-right">Պլ. քան.</th>
              <th class="text-right">iTender</th>
              <th class="text-right">Արտ.</th>
              <th class="text-right">Մնաց.</th>
              <th class="text-right">Պլ. արժ.</th>
              <th class="text-right">%</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="8" class="text-center py-4 text-muted">Տվ. չկան</td></tr>
              <tr v-for="(r,i) in rows" :key="i" v-else>
                <td><small>{{ r.cpv_code }}</small></td>
                <td style="max-width:260px">{{ r.cpv_name }}</td>
                <td class="text-right">{{ r.planned_count | numeral('0,0.##') }}</td>
                <td class="text-right text-primary">{{ r.organized_itender | numeral('0,0.##') }}</td>
                <td class="text-right text-secondary">{{ r.organized_outside | numeral('0,0.##') }}</td>
                <td class="text-right" :class="r.remaining>0?'text-warning':'text-success'">{{ r.remaining | numeral('0,0.##') }}</td>
                <td class="text-right font-weight-bold">{{ r.planned_value | numeral('0,0') }}դ.</td>
                <td class="text-right"><span class="badge" :class="pctBadge(r.execution_pct)">{{ r.execution_pct }}%</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- 1.3 Unorganized -->
    <div v-if="activeGroup==='planning' && activeTab==='unorganized'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Կազ. չէ: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>CPV</th><th>Անվ.</th><th>Չ/մ</th>
              <th class="text-right">Քան.</th>
              <th class="text-right">Մ. գ.</th>
              <th class="text-right">Արժ.</th>
              <th>Ստ.</th><th>Ժ.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="8" class="text-center py-4 text-muted">Բոլոր գնումները կազ.</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else>
                <td><small>{{ row.cpv_code }}</small></td>
                <td style="max-width:260px">{{ row.cpv_name }}</td>
                <td>{{ row.unit }}</td>
                <td class="text-right">{{ row.planned_count | numeral('0,0.##') }}</td>
                <td class="text-right">{{ row.unit_amount | numeral('0,0') }}դ.</td>
                <td class="text-right font-weight-bold text-danger">{{ row.planned_value | numeral('0,0') }}դ.</td>
                <td><small>{{ row.classifier||'—' }}</small></td>
                <td><small>{{ row.date | moment('DD.MM.YY') }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- ============================
         GROUP 2: TENDERS
         ============================ -->

    <!-- 2.1 Tender list -->
    <div v-if="activeGroup==='tenders' && activeTab==='tenders'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Ընդ.: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>#</th><th>Մրց.</th><th>Ծ.</th><th>Ստ.</th><th>Ձ.</th><th>Մաս.</th>
              <th>Հաղ.</th><th class="text-right">Հաղ. գ.</th>
              <th>Հ.</th><th>Կ.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="10" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="10" class="text-center py-4 text-muted">Տվ. չկան</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else>
                <td><small class="text-muted">{{ row.id }}</small></td>
                <td style="max-width:220px"><span :title="row.name">{{ row.name }}</span></td>
                <td><small>{{ row.code }}</small></td>
                <td><small>{{ row.subdivision }}</small></td>
                <td><small>{{ cpvTypeLabel(row.cpv_type) }}</small></td>
                <td class="text-center"><span class="badge badge-light">{{ row.participant_count }}</span></td>
                <td style="max-width:140px"><small>{{ row.winner_name }}</small></td>
                <td class="text-right">
                  <span v-if="row.winner_price">{{ row.winner_price | numeral('0,0') }}դ.</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><small>{{ row.public_date | moment('DD.MM.YY') }}</small></td>
                <td><span class="badge" :class="tenderStatusBadge(row.status)">{{ tenderStatusLabel(row.status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- 2.2 Competition analysis -->
    <div v-if="activeGroup==='tenders' && activeTab==='competition'" class="report-dashboard">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else>
        <div class="row mb-3">
          <div class="col-md-3 col-sm-6 mb-3">
            <div class="card kpi-card border-left-primary">
              <div class="card-body py-3">
                <div class="kpi-label">Մաս. միջ.</div>
                <div class="kpi-value text-primary">{{ competition.avg_participants }}</div>
                <div class="kpi-sub">մասնակից/մրցույթ</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-3">
            <div class="card kpi-card border-left-warning">
              <div class="card-body py-3">
                <div class="kpi-label">1 մաս.</div>
                <div class="kpi-value text-warning">{{ competition.one_participant_count }}</div>
                <div class="kpi-sub">{{ competition.tender_count }} մրց-ից</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-3">
            <div class="card kpi-card border-left-success">
              <div class="card-body py-3">
                <div class="kpi-label">Մրց. խն.</div>
                <div class="kpi-value text-success">{{ competition.competitive_savings | numeral('0,0') }}</div>
                <div class="kpi-sub">{{ competition.savings_pct }}% կծ-ից</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-3">
            <div class="card kpi-card border-left-info">
              <div class="card-body py-3">
                <div class="kpi-label">Ընդ. մրց.</div>
                <div class="kpi-value text-info">{{ competition.tender_count }}</div>
                <div class="kpi-sub">կազ. մրցույթ</div>
              </div>
            </div>
          </div>
        </div>
        <!-- Top winners table -->
        <div class="card">
          <div class="card-header py-2"><span class="font-weight-bold" style="font-size:.85rem">Ամ. ակ. մատ.</span></div>
          <div class="card-body p-0">
            <table class="table table-sm report-table mb-0">
              <thead><tr><th>#</th><th>Մատ.</th><th class="text-right">Հաղ.</th><th class="text-right">Ընդ. արժ.</th></tr></thead>
              <tbody>
                <tr v-if="!competition.top_winners || !competition.top_winners.length">
                  <td colspan="4" class="text-center py-3 text-muted">Տվ. չկան</td>
                </tr>
                <tr v-for="(w,i) in competition.top_winners" :key="i">
                  <td>{{ i+1 }}</td>
                  <td>{{ w.name }}</td>
                  <td class="text-right"><span class="badge badge-primary">{{ w.win_count }}</span></td>
                  <td class="text-right font-weight-bold">{{ w.total_won | numeral('0,0') }}դ.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 2.3 Cancelled tenders -->
    <div v-if="activeGroup==='tenders' && activeTab==='tenders/cancelled'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Չ. մրց.: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr><th>#</th><th>Մրց.</th><th>Ծ.</th><th>Ժ.</th><th>Պատ. չ.</th></tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="5" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="5" class="text-center py-4 text-muted">Չ. մրց. չկա</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else>
                <td><small class="text-muted">{{ row.id }}</small></td>
                <td style="max-width:260px"><span :title="row.name">{{ row.name }}</span></td>
                <td><small>{{ row.code }}</small></td>
                <td><small>{{ row.submission_date }}</small></td>
                <td style="max-width:200px"><small class="text-muted">{{ cancelReasonLabel(row.cancel_reason) }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- ============================
         GROUP 3: CONTRACTS
         ============================ -->

    <!-- 3.1 Contract Registry -->
    <div v-if="activeGroup==='contracts' && activeTab==='contracts'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Ընդ.: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>#</th><th>Ծ.</th><th>Անվ.</th><th>Մատ.</th>
              <th>Ստ. ամ.</th><th>Ավ. ամ.</th>
              <th class="text-right">Արժ.</th>
              <th class="text-right">Կատ. %</th>
              <th>Ստ.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="9" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="9" class="text-center py-4 text-muted">Պայ. չկան</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else>
                <td><small class="text-muted">{{ row.id }}</small></td>
                <td><small>{{ row.code }}</small></td>
                <td style="max-width:220px"><span :title="row.name">{{ row.name }}</span></td>
                <td style="max-width:140px"><small>{{ row.provider_name }}</small></td>
                <td><small>{{ row.sign_date | moment('DD.MM.YY') }}</small></td>
                <td><small v-if="row.end_date">{{ row.end_date }}</small><span v-else class="text-muted">—</span></td>
                <td class="text-right font-weight-bold">{{ row.price | numeral('0,0') }}դ.</td>
                <td class="text-right"><span class="badge" :class="pctBadge(row.execution_pct)">{{ row.execution_pct }}%</span></td>
                <td><span class="badge" :class="contractSignBadge(row.is_sign)">{{ contractSignLabel(row.is_sign) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>

    <!-- 3.2 Contract Execution -->
    <div v-if="activeGroup==='contracts' && activeTab==='contracts/execution'">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else>
        <!-- Summary KPIs -->
        <div v-if="execSummary" class="row mb-3">
          <div class="col-md-3 col-sm-6 mb-2">
            <div class="card kpi-card border-left-primary">
              <div class="card-body py-3">
                <div class="kpi-label">Պlа.</div>
                <div class="kpi-value text-primary">{{ execSummary.total_planned | numeral('0,0') }}</div>
                <div class="kpi-sub">դ.</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-2">
            <div class="card kpi-card border-left-success">
              <div class="card-body py-3">
                <div class="kpi-label">Մat.</div>
                <div class="kpi-value text-success">{{ execSummary.total_supplied | numeral('0,0') }}</div>
                <div class="kpi-sub">դ.</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-2">
            <div class="card kpi-card border-left-warning">
              <div class="card-body py-3">
                <div class="kpi-label">Մn.</div>
                <div class="kpi-value text-warning">{{ execSummary.total_remaining | numeral('0,0') }}</div>
                <div class="kpi-sub">դ.</div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-sm-6 mb-2">
            <div class="card kpi-card border-left-info">
              <div class="card-body py-3">
                <div class="kpi-label">Կat. %</div>
                <div class="kpi-value text-info">{{ execSummary.avg_execution }}</div>
                <div class="kpi-sub">միջ. %</div>
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-sm report-table mb-0">
                <thead><tr>
                  <th>#</th><th>Ծ.</th><th>Անվ.</th><th>Մat.</th>
                  <th class="text-right">Պlа.</th>
                  <th class="text-right">Մat.</th>
                  <th class="text-right">Մn.</th>
                  <th class="text-right">%</th>
                </tr></thead>
                <tbody>
                  <tr v-if="!rows.length"><td colspan="8" class="text-center py-3 text-muted">Տvl. չkаn</td></tr>
                  <tr v-for="r in rows" :key="r.id">
                    <td><small class="text-muted">{{ r.id }}</small></td>
                    <td><small>{{ r.code }}</small></td>
                    <td style="max-width:200px">{{ r.name }}</td>
                    <td style="max-width:130px"><small>{{ r.provider_name }}</small></td>
                    <td class="text-right">{{ r.planned_value | numeral('0,0') }}դ.</td>
                    <td class="text-right text-success">{{ r.supplied_value | numeral('0,0') }}դ.</td>
                    <td class="text-right text-warning">{{ r.remaining_value | numeral('0,0') }}դ.</td>
                    <td class="text-right"><span class="badge" :class="pctBadge(r.execution_pct)">{{ r.execution_pct }}%</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex justify-content-center py-3">
              <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3.3 Overdue contracts -->
    <div v-if="activeGroup==='contracts' && activeTab==='contracts/overdue'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-danger mr-2"><i class="mdi mdi-alert-outline"></i></small>
          <small class="text-muted">Ժmk. անց.: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>#</th><th>Ծ.</th><th>Անվ.</th><th>Մat.</th>
              <th>Ավ. ժ.</th>
              <th class="text-right">Պtv.</th>
              <th class="text-right">Մat.</th>
              <th class="text-right">Ժ.aw.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="text-center py-4"><div class="spinner-border spinner-border-sm text-danger"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="8" class="text-center py-4 text-success"><i class="mdi mdi-check-circle mr-1"></i>Ժmk. aнц. չkа</td></tr>
              <tr v-for="row in rows" :key="row.id" v-else class="table-danger-soft">
                <td><small class="text-muted">{{ row.id }}</small></td>
                <td><small>{{ row.code }}</small></td>
                <td style="max-width:200px">{{ row.name }}</td>
                <td><small>{{ row.provider_name }}</small></td>
                <td><small class="text-danger font-weight-bold">{{ row.delivery_date }}</small></td>
                <td class="text-right">{{ row.ordered_qty }}</td>
                <td class="text-right text-warning">{{ row.supplied_qty }}</td>
                <td class="text-right text-danger font-weight-bold">{{ row.overdue_value | numeral('0,0') }}դ.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 3.4 Savings -->
    <div v-if="activeGroup==='contracts' && activeTab==='contracts/savings'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Ծ. ն.: <strong>{{ total }}</strong></small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>Ծ.Ն.</th><th>Ծ.Ա.</th>
              <th class="text-right">Պlа. արժ.</th>
              <th class="text-right">Пay. արժ.</th>
              <th class="text-right">Xna.</th>
              <th class="text-right">Xna. %</th>
              <th class="text-right">Пay. #</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="7" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="7" class="text-center py-4 text-muted">Տvl. չkаn</td></tr>
              <tr v-for="(r,i) in rows" :key="i" v-else>
                <td><small>{{ r.procurement_year }}</small></td>
                <td style="max-width:200px"><span :title="r.procurement_name">{{ r.procurement_name }}</span></td>
                <td class="text-right">{{ r.planned_value | numeral('0,0') }}դ.</td>
                <td class="text-right">{{ r.contract_value | numeral('0,0') }}դ.</td>
                <td class="text-right font-weight-bold" :class="r.savings>=0?'text-success':'text-danger'">{{ r.savings | numeral('0,0') }}դ.</td>
                <td class="text-right"><span class="badge" :class="pctBadge(r.savings_pct)">{{ r.savings_pct }}%</span></td>
                <td class="text-right">{{ r.contract_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ============================
         GROUP 4: EXECUTIVE DASHBOARD
         ============================ -->
    <div v-if="activeGroup==='dashboard'">
      <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
      <div v-else>
        <!-- 4.1 KPIs + 4.4 Efficiency -->
        <div class="row mb-3">
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-primary">
              <div class="card-body py-2">
                <div class="kpi-label">Ծ. արժ.</div>
                <div class="kpi-value text-primary" style="font-size:1.1rem">{{ dashboard.planned_value | numeral('0,0') }}</div>
                <div class="kpi-sub">ՀՀ դ.</div>
              </div>
            </div>
          </div>
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-info">
              <div class="card-body py-2">
                <div class="kpi-label">Akt. մrcuyt.</div>
                <div class="kpi-value text-info" style="font-size:1.1rem">{{ dashboard.organized_count }}</div>
                <div class="kpi-sub">{{ dashboard.cancelled_count }} չegh.</div>
              </div>
            </div>
          </div>
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-success">
              <div class="card-body py-2">
                <div class="kpi-label">Pay. արժ.</div>
                <div class="kpi-value text-success" style="font-size:1.1rem">{{ dashboard.contract_value | numeral('0,0') }}</div>
                <div class="kpi-sub">{{ dashboard.contract_count }} pay.</div>
              </div>
            </div>
          </div>
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-warning">
              <div class="card-body py-2">
                <div class="kpi-label">Xnayogh.</div>
                <div class="kpi-value text-warning" style="font-size:1.1rem">{{ dashboard.savings | numeral('0,0') }}</div>
                <div class="kpi-sub">{{ dashboard.savings_pct }}%</div>
              </div>
            </div>
          </div>
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-secondary">
              <div class="card-body py-2">
                <div class="kpi-label">Mij. tewugh.</div>
                <div class="kpi-value" style="font-size:1.1rem">{{ dashboard.avg_duration }}</div>
                <div class="kpi-sub">oror</div>
              </div>
            </div>
          </div>
          <div class="col-md-2 col-sm-4 mb-2">
            <div class="card kpi-card border-left-danger">
              <div class="card-body py-2">
                <div class="kpi-label">Plan. durc.</div>
                <div class="kpi-value text-danger" style="font-size:1.1rem">{{ dashboard.off_plan_pct }}%</div>
                <div class="kpi-sub">mij. {{ dashboard.avg_participants }} mas.</div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- 4.2 Top Goods -->
          <div class="col-md-6 mb-3">
            <div class="card h-100">
              <div class="card-header py-2"><span class="font-weight-bold" style="font-size:.85rem">4.2 Am.sh. gnvog apranqner (TOP 15)</span></div>
              <div class="card-body p-0">
                <div v-if="topGoodsLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>
                <table v-else class="table table-sm report-table mb-0">
                  <thead><tr><th>#</th><th>CPV</th><th>Ан.</th><th class="text-right">Mrц.</th><th class="text-right">Arj.</th></tr></thead>
                  <tbody>
                    <tr v-if="!topGoods.length"><td colspan="5" class="text-center py-3 text-muted">Tvl. чkаn</td></tr>
                    <tr v-for="(g,i) in topGoods" :key="i">
                      <td>{{ i+1 }}</td>
                      <td><small>{{ g.code }}</small></td>
                      <td style="max-width:160px"><span :title="g.name">{{ g.name }}</span></td>
                      <td class="text-right"><span class="badge badge-primary">{{ g.tender_count }}</span></td>
                      <td class="text-right">{{ g.total_value | numeral('0,0') }}դ.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- 4.3 Supplier Activity -->
          <div class="col-12 mb-3">
            <div class="card">
              <div class="card-header py-2"><span class="font-weight-bold" style="font-size:.85rem">4.3 Մատակարարների ակտիվություն (TOP 15)</span></div>
              <div class="card-body p-0">
                <div v-if="topSuppliersLoading" class="text-center py-3"><div class="spinner-border spinner-border-sm text-primary"></div></div>
                <table v-else class="table table-sm report-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Մատ.</th>
                      <th class="text-right">Մաս.</th>
                      <th class="text-right">🥇</th>
                      <th class="text-right">🥈</th>
                      <th class="text-right">🥉</th>
                      <th class="text-right">Հաղ. %</th>
                      <th class="text-right">Պայ. արժ.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!topSuppliers.length"><td colspan="8" class="text-center py-3 text-muted">Տվ. չկան</td></tr>
                    <tr v-for="(s,i) in topSuppliers" :key="i">
                      <td>{{ i+1 }}</td>
                      <td style="max-width:220px">{{ s.name }}</td>
                      <td class="text-right"><span class="badge badge-primary">{{ s.participation }}</span></td>
                      <td class="text-right"><span class="badge badge-success">{{ s.place_1 }}</span></td>
                      <td class="text-right"><span class="badge badge-secondary">{{ s.place_2 }}</span></td>
                      <td class="text-right"><span class="badge badge-secondary">{{ s.place_3 }}</span></td>
                      <td class="text-right"><span class="badge" :class="pctBadge(s.win_pct)">{{ s.win_pct }}%</span></td>
                      <td class="text-right font-weight-bold">{{ s.total_value | numeral('0,0') }}դ.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 4.1 Monthly (last 12m) -->
        <div class="card">
          <div class="card-header py-2"><span class="font-weight-bold" style="font-size:.85rem">Ам.ам. pay. (12 amsem)</span></div>
          <div class="card-body p-0">
            <table class="table table-sm report-table mb-0">
              <thead><tr><th>Ами.</th><th class="text-right">Pay. #</th><th class="text-right">Yndh. arj.</th></tr></thead>
              <tbody>
                <tr v-if="!dashboard.monthly || !dashboard.monthly.length">
                  <td colspan="3" class="text-center py-3 text-muted">Tvl. чkаn</td>
                </tr>
                <tr v-for="(m,i) in dashboard.monthly" :key="i">
                  <td>{{ m.month }}</td>
                  <td class="text-right">{{ m.count }}</td>
                  <td class="text-right font-weight-bold">{{ m.value | numeral('0,0') }}դ.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================
         GROUP 5: LIFECYCLE
         ============================ -->
    <div v-if="activeGroup==='lifecycle'" class="card">
      <div class="card-body p-0">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <small class="text-muted">Շrchkayin Report — <strong>{{ total }}</strong> tox</small>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm report-table mb-0">
            <thead><tr>
              <th>CPV</th><th>Ан.</th><th>Ст.</th>
              <th class="text-right">Пl.արժ.</th>
              <th>Мrcuyt</th><th>Hagh.</th>
              <th class="text-right">Հаgh. g.</th>
              <th>Pay.</th>
              <th class="text-right">Pay. g.</th>
              <th class="text-right">Мnat.</th>
              <th class="text-right">Xna.</th>
              <th>Aw.j.</th>
              <th>Կar.</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="13" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></td></tr>
              <tr v-else-if="!rows.length"><td colspan="13" class="text-center py-4 text-muted">Tvl. чkаn</td></tr>
              <tr v-for="row in rows" :key="row.plan_detail_id" v-else>
                <td><small>{{ row.cpv_code }}</small></td>
                <td style="max-width:160px"><span :title="row.cpv_name">{{ row.cpv_name }}</span></td>
                <td><small>{{ row.subdivision }}</small></td>
                <td class="text-right">{{ row.planned_value | numeral('0,0') }}դ.</td>
                <td style="max-width:120px"><small>{{ row.tender_name }}</small></td>
                <td style="max-width:100px"><small>{{ row.winner_name }}</small></td>
                <td class="text-right">
                  <span v-if="row.winner_price">{{ row.winner_price | numeral('0,0') }}դ.</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><small>{{ row.contract_code }}</small></td>
                <td class="text-right">
                  <span v-if="row.contract_price">{{ row.contract_price | numeral('0,0') }}դ.</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-right">
                  <span v-if="row.ordered_qty>0">{{ row.supplied_qty }}/{{ row.ordered_qty }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-right">
                  <span v-if="row.savings !== null" :class="row.savings>=0?'text-success':'text-danger'" class="font-weight-bold">{{ row.savings | numeral('0,0') }}դ.</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><small>{{ row.delivery_date||'—' }}</small></td>
                <td><span class="badge" :class="lifecycleBadge(row.lifecycle_status)">{{ lifecycleLabel(row.lifecycle_status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center py-3">
          <report-pagination :total="total" :per-page="perPage" :page.sync="page" @change="loadData" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportFilters from './partials/ReportFilters'
import ColumnSelector from './partials/ColumnSelector'
import ReportPagination from './partials/ReportPagination'

const GROUP_DEFAULT_TABS = {
  planning:  'planning',
  tenders:   'tenders',
  contracts: 'contracts',
  dashboard: 'dashboard',
  lifecycle: 'lifecycle',
}

const ENDPOINTS = {
  'planning':           'reports/planning',
  'plan-vs-organized':  'reports/plan-vs-organized',
  'unorganized':        'reports/unorganized',
  'tenders':            'reports/tenders',
  'competition':        'reports/tenders/competition',
  'tenders/cancelled':  'reports/tenders/cancelled',
  'contracts':          'reports/contracts',
  'contracts/execution':'reports/contracts/execution',
  'contracts/overdue':  'reports/contracts/overdue',
  'contracts/savings':  'reports/contracts/savings',
  'dashboard':          'reports/dashboard',
  'top-goods':          'reports/top-goods',
  'top-suppliers':      'reports/top-suppliers',
  'lifecycle':          'reports/lifecycle',
}

export default {
  name: 'ReportsIndex',
  components: { ReportFilters, ColumnSelector, ReportPagination },
  data() {
    return {
      activeGroup: 'planning',
      activeTab:   'planning',
      rows:        [],
      total:       0,
      page:        1,
      perPage:     15,
      loading:     false,
      currentFilters: {},
      dashboard: {
        planned_value: 0, organized_count: 0, cancelled_count: 0,
        contract_count: 0, contract_value: 0, savings: 0, savings_pct: 0,
        avg_duration: 0, avg_participants: 0, off_plan_pct: 0, monthly: []
      },
      competition: {
        avg_participants: 0, one_participant_count: 0, tender_count: 0,
        competitive_savings: 0, savings_pct: 0, top_winners: []
      },
      execSummary: null,
      topGoods: [],
      topSuppliers: [],
      topGoodsLoading: false,
      topSuppliersLoading: false,
      groups: [
        { key: "planning",  label: 'Պլանավ.' },
        { key: "tenders",   label: 'Մրցույ.' },
        { key: "contracts", label: 'Պայմ.' },
        { key: "dashboard", label: 'Ղեկավար.' },
        { key: "lifecycle", label: "Lifecycle" },
      ],
      groupTabs: {
        planning:  [
          { key: 'planning',         label: 'Գնումների Պլան' },
          { key: 'plan-vs-organized',label: 'Պլա / Կազ.' },
          { key: 'unorganized',      label: 'Չկազմ.' },
        ],
        tenders: [
          { key: 'tenders',           label: 'Մրցույթներ' },
          { key: 'competition',       label: 'Մրցկ.' },
          { key: 'tenders/cancelled', label: 'Չեգհ.' },
        ],
        contracts: [
          { key: 'contracts',           label: 'Ռեեստր' },
          { key: 'contracts/execution', label: 'Կատարում' },
          { key: 'contracts/overdue',   label: 'ժամկ.' },
          { key: 'contracts/savings',   label: 'Խնայ.' },
        ],
        dashboard: [],
        lifecycle: [],
      },
      allColumns: {
        planning: [
          { key: 'cpv_code',          label: 'CPV' },
          { key: 'cpv_name',          label: 'Ан.' },
          { key: 'unit',              label: 'Ч/м' },
          { key: 'planned_count',     label: 'Пl.кn.' },
          { key: 'unit_amount',       label: 'М.г.' },
          { key: 'planned_value',     label: 'Пl.арж.' },
          { key: 'organized_itender', label: 'iTender' },
          { key: 'organized_outside', label: 'Арт.' },
          { key: 'remaining',         label: 'Мнац.' },
          { key: 'execution_pct',     label: '%' },
          { key: 'classifier',        label: 'Ст.' },
          { key: 'status',            label: 'К.' },
        ],
      },
      visibleColumns: {
        planning: null,
      },
    }
  },
  computed: {
    currentGroupTabs() {
      return this.groupTabs[this.activeGroup] || []
    },
  },
  mounted() {
    this.visibleColumns.planning = this.loadCols('rpt_planning', ['cpv_code','cpv_name','unit','planned_count','planned_value','organized_itender','remaining','execution_pct','status'])
    this.loadData()
  },
  methods: {
    cpvTypeLabel(t) {
      return { 1: 'Ապրանք', 2: 'Ծառայություն', 3: 'Աշխատանք' }[t] || ""
    },
    loadCols(key, defaults) {
      try {
        const s = localStorage.getItem(key)
        return s ? JSON.parse(s) : defaults
      } catch(e) { return defaults }
    },
    isCol(group, key) {
      return this.visibleColumns[group] && this.visibleColumns[group].includes(key)
    },
    setGroup(group) {
      this.activeGroup = group
      this.activeTab   = GROUP_DEFAULT_TABS[group]
      this.page        = 1
      this.rows        = []
      this.total       = 0
      this.execSummary = null
      this.loadData()
      if (group === 'dashboard') {
        this.loadTopGoods()
        this.loadTopSuppliers()
      }
    },
    setTab(tab) {
      this.activeTab = tab
      this.page      = 1
      this.rows      = []
      this.execSummary = null
      this.loadData()
    },
    onFilter(filters) {
      this.currentFilters = filters
      this.page           = 1
      this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const endpoint = ENDPOINTS[this.activeTab]
        if (!endpoint) { this.loading = false; return }

        const { data } = await this.$client.get(process.env.VUE_APP_API_URL + endpoint, {
          params: { ...this.currentFilters, page: this.page, limit: this.perPage },
        })

        if (this.activeGroup === 'dashboard') {
          this.dashboard = data.data || this.dashboard
          this.rows = []; this.total = 0
        } else if (this.activeTab === 'competition') {
          this.competition = data.data || this.competition
          this.rows = []; this.total = 0
        } else if (this.activeTab === 'contracts/execution') {
          this.execSummary = data.summary || null
          this.rows  = data.data  || []
          this.total = data.count || 0
        } else {
          this.rows  = data.data  || []
          this.total = data.count || 0
        }
      } catch(e) {
        this.rows  = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadTopGoods() {
      this.topGoodsLoading = true
      try {
        const { data } = await this.$client.get(process.env.VUE_APP_API_URL + 'reports/top-goods')
        this.topGoods = data.data || []
      } catch(e) { this.topGoods = [] }
      finally { this.topGoodsLoading = false }
    },
    async loadTopSuppliers() {
      this.topSuppliersLoading = true
      try {
        const { data } = await this.$client.get(process.env.VUE_APP_API_URL + 'reports/top-suppliers')
        this.topSuppliers = data.data || []
      } catch(e) { this.topSuppliers = [] }
      finally { this.topSuppliersLoading = false }
    },
    async exportExcel() {
      const params = new URLSearchParams({ ...this.currentFilters })
      window.open(process.env.VUE_APP_API_URL + 'reports/export/planning?' + params.toString(), '_blank')
    },
    pctBadge(pct) {
      if (pct >= 80) return 'badge-success'
      if (pct >= 40) return 'badge-warning'
      return 'badge-danger'
    },
    planStatusBadge(s) {
      const bm = { 0: 'badge-secondary', 1: 'badge-primary', 2: 'badge-warning', 3: 'badge-success', 4: 'badge-danger' }
      return bm[s] || 'badge-secondary'
    },
    planStatusLabel(s) {
      const lm = { 0: 'Պլան.', 1: 'Կազմ.', 2: 'Պայմ.', 3: 'Կատ.', 4: 'Չեգհ.' }
      return lm[s] || '—'
    },
    tenderStatusBadge(s) {
      return { 0: 'badge-secondary', 1: 'badge-info', 2: 'badge-primary', 3: 'badge-success', 4: 'badge-danger' }[s] || 'badge-secondary'
    },
    tenderStatusLabel(s) {
      return { 0: 'Nax.', 1: 'Hrap.', 2: 'Yndm.', 3: 'Аv.', 4: 'Ch.' }[s] || '—'
    },
    contractSignBadge(isSigned) { return isSigned ? 'badge-success' : 'badge-warning' },
    contractSignLabel(isSigned) { return isSigned ? 'Ст.' : 'Ст. ч.' },
    lifecycleBadge(s) {
      const m = {
        planned:    'badge-secondary',
        organizing: 'badge-primary',
        contract:   'badge-warning',
        completed:  'badge-success',
        cancelled:  'badge-danger',
      }
      return m[s] || 'badge-secondary'
    },
    lifecycleLabel(s) {
      const m = {
        planned:    'Пl.',
        organizing: 'Кaz.',
        contract:   'Pay.',
        completed:  'Кat.',
        cancelled:  'Чgh.',
      }
      return m[s] || '—'
    },
    cancelReasonLabel(r) {
      return r === 'not_requirement_purchase' ? 'Pal. bac. ч.' : r === 'need_change' ? 'Pal. pox.' : r || '—'
    },
  },
}
</script>

<style scoped>
.report-group-nav .nav-link {
  font-size: 0.8rem;
  border-radius: 20px;
  color: #6c757d;
  border: 1px solid #dee2e6;
  margin-right: 4px;
  padding: 4px 14px;
}
.report-group-nav .nav-link.active {
  background: #4a90e2;
  border-color: #4a90e2;
  color: #fff;
  font-weight: 600;
}
.report-table th {
  font-size: 0.72rem;
  font-weight: 600;
  background: #f8f9fa;
  color: #495057;
  border-top: none;
  white-space: nowrap;
  padding: 7px 10px;
}
.report-table td {
  font-size: 0.78rem;
  padding: 5px 10px;
  vertical-align: middle;
}
.report-table tr:hover { background: #f0f4ff; }
.table-danger-soft { background: #fff5f5; }
.kpi-card { border-radius: 8px; }
.kpi-card.border-left-primary   { border-left: 4px solid #4a90e2 !important; }
.kpi-card.border-left-info      { border-left: 4px solid #17a2b8 !important; }
.kpi-card.border-left-success   { border-left: 4px solid #28a745 !important; }
.kpi-card.border-left-warning   { border-left: 4px solid #ffc107 !important; }
.kpi-card.border-left-danger    { border-left: 4px solid #dc3545 !important; }
.kpi-card.border-left-secondary { border-left: 4px solid #6c757d !important; }
.kpi-label { font-size: 0.68rem; color: #6c757d; text-transform: uppercase; margin-bottom: 2px; }
.kpi-value { font-size: 1.3rem; font-weight: 700; line-height: 1.2; }
.kpi-sub   { font-size: 0.68rem; color: #6c757d; }
.gap-2 { gap: 0.5rem; }
</style>